import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPlay, faRotateLeft } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import {
	CLIP_NUDGE_SECONDS,
	CLIP_PRECISION_SECONDS,
	DEFAULT_AUDIO_CONFIG,
	MIN_CLIP_SECONDS,
	StoryboardScene,
	SubtitlePosition,
	SubtitleSize,
	SubtitleStyle,
	WizardTimelineClip,
} from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import { formatSeconds } from "../wizard.utils";
import { VoiceTrack } from "./VoiceTrack";

interface TrimEditorProps {
	clip: WizardTimelineClip;
	scene: StoryboardScene;
	/** Where this clip sits in the finished video — the caption says "Szene N". */
	sceneNumber: number;
	disabled?: boolean;
	/**
	 * The author's step-3 subtitle choice. The preview renders its live captions in that
	 * style, position and size — what the export will burn is what the preview shows.
	 */
	subtitles?: { style: SubtitleStyle; position: SubtitlePosition; size: SubtitleSize };
	onChange: (next: Partial<WizardTimelineClip>) => void;
	/** Fired when an interaction ends, so the save debounce starts from a settled value. */
	onCommit: () => void;
}

/** Roughly a classic subtitle line; cues break here or at a sentence's end. */
const CAPTION_LINE_CHARS = 42;

/** The grid the server enforces: everything lands on a tenth of a second. */
const snap = (seconds: number) =>
	Math.round(seconds / CLIP_PRECISION_SECONDS) * CLIP_PRECISION_SECONDS;

/**
 * The cut for one clip: two handles, a hatched region for what is thrown away, and the
 * ±0,2 s buttons that make the same edit without a mouse.
 *
 * The clamping here mirrors the API's invariants exactly — at least two seconds kept, never
 * past the end of the scene, always on the tenth-of-a-second grid — but it is a convenience
 * rather than the rule. The server checks all three again and refuses rather than rounds,
 * so a handle that could be dragged into an invalid cut would only produce a 400 later.
 *
 * Pointer events rather than mouse events, so the handles work under a finger as well as a
 * cursor; the mockup uses `mousedown` and would simply not respond on a tablet.
 */
export const TrimEditor: FunctionComponent<TrimEditorProps> = ({
	clip,
	scene,
	sceneNumber,
	disabled,
	subtitles,
	onChange,
	onCommit,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) =>
			t(`videoWizard.steps.cutExport.${key}`, options ?? {}),
		[t]
	);

	const trackRef = useRef<HTMLDivElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	// The narration element lives in the VoiceTrack below; held here too so the trim
	// preview can start the voice in sync with the picture.
	const narrationRef = useRef<HTMLAudioElement | null>(null);
	// True only while a "Zuschnitt ansehen" run is on: the video's native controls and the
	// voice line's own play button keep playing their track alone.
	const withVoice = useRef(false);
	const voiceStarted = useRef(false);
	const [dragging, setDragging] = useState<"in" | "out" | null>(null);

	const duration = scene.durationSeconds;
	const kept = Math.round((clip.outSeconds - clip.inSeconds) * 10) / 10;
	const inPercent = (clip.inSeconds / duration) * 100;
	const outPercent = (clip.outSeconds / duration) * 100;

	/** One handle's new position, clamped into what the server will accept. */
	const moveHandle = useCallback(
		(handle: "in" | "out", seconds: number) => {
			const value = snap(seconds);

			if (handle === "in") {
				onChange({
					inSeconds: Math.min(Math.max(0, value), snap(clip.outSeconds - MIN_CLIP_SECONDS)),
					outSeconds: clip.outSeconds,
				});

				return;
			}

			onChange({
				inSeconds: clip.inSeconds,
				outSeconds: Math.max(Math.min(duration, value), snap(clip.inSeconds + MIN_CLIP_SECONDS)),
			});
		},
		[clip.inSeconds, clip.outSeconds, duration, onChange]
	);

	// Dragging is tracked on the window rather than the handle, so a fast drag that leaves
	// the element behind keeps following the pointer instead of stopping where it escaped.
	useEffect(() => {
		if (!dragging) {
			return undefined;
		}

		const onMove = (event: PointerEvent) => {
			const track = trackRef.current;

			if (!track) {
				return;
			}

			const rect = track.getBoundingClientRect();

			moveHandle(dragging, ((event.clientX - rect.left) / rect.width) * duration);
		};
		const onUp = () => {
			setDragging(null);
			onCommit();
		};

		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onUp);
		window.addEventListener("pointercancel", onUp);

		return () => {
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onUp);
			window.removeEventListener("pointercancel", onUp);
		};
	}, [dragging, duration, moveHandle, onCommit]);

	const nudge = (handle: "in" | "out", delta: number) => {
		moveHandle(handle, (handle === "in" ? clip.inSeconds : clip.outSeconds) + delta);
		onCommit();
	};

	// The choice as the export will resolve it — the preview must not show a different
	// caption than the burn.
	const subtitleStyle = subtitles?.style ?? DEFAULT_AUDIO_CONFIG.subtitleStyle;
	const subtitlePosition = subtitles?.position ?? DEFAULT_AUDIO_CONFIG.subtitlePosition;
	const subtitleSize = subtitles?.size ?? DEFAULT_AUDIO_CONFIG.subtitleSize;

	/**
	 * The caption cues, cut from the take's word timings the way the export cuts its
	 * burned subtitles: a cue breaks at a sentence's end or once the line is full. The
	 * words keep their own timings — that is what the word-highlight style sweeps —
	 * on the take's clock, which is the narration audio's clock.
	 */
	const captionCues = useMemo(() => {
		const words = scene.narrationAlignment ?? [];
		const cues: { start: number; end: number; words: typeof words }[] = [];
		let current: typeof words = [];

		for (const word of words) {
			current.push(word);
			const length = current.reduce((sum, w) => sum + w.word.length + 1, -1);

			if (length >= CAPTION_LINE_CHARS || /[.!?…]$/.test(word.word)) {
				cues.push({ start: current[0].startSeconds, end: word.endSeconds, words: current });
				current = [];
			}
		}

		if (current.length) {
			cues.push({
				start: current[0].startSeconds,
				end: current[current.length - 1].endSeconds,
				words: current,
			});
		}

		return cues;
	}, [scene.narrationAlignment]);

	const [caption, setCaption] = useState<{
		words: { word: string; startSeconds: number; endSeconds: number }[];
		spokenIndex: number;
	} | null>(null);

	// Captions follow the narration audio, whoever started it — the combined trim
	// preview or the voice line's own play button. An older take without word timings
	// simply shows none; regenerating the narration brings them.
	useEffect(() => {
		const audio = narrationRef.current;

		if (!audio || subtitleStyle === SubtitleStyle.NONE || !captionCues.length) {
			setCaption(null);

			return undefined;
		}

		const onTimeUpdate = () => {
			const now = audio.currentTime;
			const cue = captionCues.find((entry) => now >= entry.start && now <= entry.end);

			if (!cue) {
				setCaption(null);

				return;
			}

			// The word the voice is on — the last one whose start has passed, so the
			// highlight rests on a word through the pause after it rather than vanishing.
			let spokenIndex = 0;

			for (const [index, word] of cue.words.entries()) {
				if (now >= word.startSeconds) {
					spokenIndex = index;
				}
			}

			setCaption({ words: cue.words, spokenIndex });
		};
		const onStop = () => setCaption(null);

		audio.addEventListener("timeupdate", onTimeUpdate);
		audio.addEventListener("pause", onStop);
		audio.addEventListener("ended", onStop);

		return () => {
			audio.removeEventListener("timeupdate", onTimeUpdate);
			audio.removeEventListener("pause", onStop);
			audio.removeEventListener("ended", onStop);
		};
	}, [subtitleStyle, captionCues, scene.narrationAssetUrl]);

	/**
	 * Plays the kept region with the voice where the author placed it — a feel for the
	 * export's mix, from the same two elements the single-track buttons play alone. The
	 * voice joins once the picture reaches its offset, from the take's own in-point.
	 */
	const playTrimmed = () => {
		const video = videoRef.current;

		if (!video) {
			return;
		}

		const audio = narrationRef.current;

		if (audio) {
			audio.pause();
			audio.currentTime = clip.narrationInSeconds ?? 0;
		}

		withVoice.current = !!audio;
		voiceStarted.current = false;
		video.currentTime = clip.inSeconds;
		void video.play();
	};

	// The preview is a plain <video>; stopping it at the out point is what makes it show
	// the cut rather than the whole clip. During a with-voice run this is also the clock
	// the narration starts by, and any pause — the out point, or the author's — stops both.
	useEffect(() => {
		const video = videoRef.current;

		if (!video) {
			return undefined;
		}

		const onTimeUpdate = () => {
			if (withVoice.current && !voiceStarted.current) {
				const audio = narrationRef.current;
				const offset = clip.narrationOffsetSeconds ?? 0;

				if (audio && video.currentTime - clip.inSeconds >= offset) {
					voiceStarted.current = true;
					void audio.play();
				}
			}

			if (video.currentTime >= clip.outSeconds) {
				video.pause();
				video.currentTime = clip.inSeconds;
			}
		};
		const onPause = () => {
			if (withVoice.current) {
				withVoice.current = false;
				voiceStarted.current = false;
				narrationRef.current?.pause();
			}
		};

		video.addEventListener("timeupdate", onTimeUpdate);
		video.addEventListener("pause", onPause);

		return () => {
			video.removeEventListener("timeupdate", onTimeUpdate);
			video.removeEventListener("pause", onPause);
		};
	}, [clip.inSeconds, clip.outSeconds, clip.narrationOffsetSeconds, clip.narrationInSeconds]);

	// The accent carries the handle and the glyph stays dark — `accent` is a fill, never an
	// ink, and a white glyph on it clears only 3.1:1.
	const handleClasses =
		"absolute top-0 z-10 flex h-full w-4 -translate-x-1/2 cursor-ew-resize touch-none items-center justify-center rounded bg-accent text-[10px] font-bold text-ink shadow";

	return (
		<section className="space-y-3 rounded-card border border-border-soft bg-surface p-4">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="text-sm font-bold text-ink">
					{ts("trimHeading", { number: sceneNumber, title: scene.title })}
				</span>
				<div className="flex flex-wrap gap-2">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={!scene.video?.assetUrl}
						image={<Icon className="mr-2" icon={faPlay} />}
						size={ButtonSize.S}
						title={ts("playTrim")}
						type="button"
						onClick={playTrimmed}
					/>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={disabled}
						image={<Icon className="mr-2" icon={faRotateLeft} />}
						size={ButtonSize.S}
						title={ts("resetClip")}
						type="button"
						onClick={() => {
							onChange({ inSeconds: 0, outSeconds: duration });
							onCommit();
						}}
					/>
				</div>
			</div>

			{!!scene.video?.assetUrl && (
				// The caption overlay is live, not a <track>: it follows the narration audio's
				// clock and approximates the classic style — the burned subtitles in the export
				// stay the authoritative rendering.
				// The wrapper hugs the picture (w-fit), so the caption's percentages measure
				// the actual frame — and both sit centred in the panel.
				<div className="relative mx-auto w-fit max-w-full">
					<video
						ref={videoRef}
						controls
						className="video-own-size max-h-[320px] w-auto max-w-full rounded-card bg-ink"
						preload="metadata"
						src={scene.video.assetUrl}
					/>
					{!!caption && (
						// Rendered as the export will burn it: the chosen position (the same 6 %
						// margins as the .ass style), the chosen size, and for word-highlight the
						// sweep over the words the voice is on. Centred by inline translate so no
						// stylesheet state can move it.
						<span
							className="pointer-events-none absolute max-w-[85%] rounded bg-ink/80 px-2 py-0.5 text-center font-semibold text-white"
							style={{
								...(subtitlePosition === SubtitlePosition.TOP_CENTER
									? { top: "6%" }
									: { bottom: "6%" }),
								...(subtitlePosition === SubtitlePosition.BOTTOM_LEFT
									? { left: "6%" }
									: { left: "50%", transform: "translateX(-50%)" }),
								fontSize:
									subtitleSize === SubtitleSize.S ? 11 : subtitleSize === SubtitleSize.L ? 17 : 13,
							}}
						>
							{subtitleStyle === SubtitleStyle.WORD_HIGHLIGHT
								? caption.words.map((word, index) => (
										<span
											key={`${word.startSeconds}-${index}`}
											className={
												index === caption.spokenIndex
													? "rounded bg-accent-tint px-0.5 text-text"
													: undefined
											}
										>
											{word.word}
											{index < caption.words.length - 1 ? " " : ""}
										</span>
									))
								: caption.words.map((word) => word.word).join(" ")}
						</span>
					)}
				</div>
			)}

			{/* The picture. The kept region is bright, the cut regions are hatched. */}
			<div className="relative py-3">
				<div
					ref={trackRef}
					className="relative h-14 w-full overflow-hidden rounded-card bg-accent-tint"
				>
					<div
						className="absolute inset-y-0 left-0 bg-border-soft/80 bg-[repeating-linear-gradient(45deg,transparent,transparent_5px,rgba(0,0,0,.10)_5px,rgba(0,0,0,.10)_10px)]"
						style={{ width: `${inPercent}%` }}
					/>
					<div
						className="absolute inset-y-0 right-0 bg-border-soft/80 bg-[repeating-linear-gradient(45deg,transparent,transparent_5px,rgba(0,0,0,.10)_5px,rgba(0,0,0,.10)_10px)]"
						style={{ width: `${100 - outPercent}%` }}
					/>
					<div
						className="absolute inset-y-0 border-y-2 border-accent bg-accent/20"
						style={{ left: `${inPercent}%`, right: `${100 - outPercent}%` }}
					/>
				</div>

				{(["in", "out"] as const).map((handle) => {
					const percent = handle === "in" ? inPercent : outPercent;
					const seconds = handle === "in" ? clip.inSeconds : clip.outSeconds;

					return (
						<div
							key={handle}
							aria-label={ts(handle === "in" ? "handleIn" : "handleOut")}
							aria-valuemax={duration}
							aria-valuemin={0}
							aria-valuenow={seconds}
							aria-valuetext={formatSeconds(seconds)}
							className={classNames(handleClasses, disabled && "pointer-events-none opacity-50")}
							role="slider"
							style={{ left: `${percent}%`, top: "0.75rem", height: "3.5rem" }}
							tabIndex={disabled ? -1 : 0}
							onKeyDown={(event) => {
								// The arrow keys are the third way to make this edit, after the
								// handle and the buttons — a slider nobody can operate from the
								// keyboard is not a slider.
								if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
									event.preventDefault();
									nudge(
										handle,
										event.key === "ArrowLeft" ? -CLIP_PRECISION_SECONDS : CLIP_PRECISION_SECONDS
									);
								}
							}}
							onPointerDown={(event) => {
								if (disabled) {
									return;
								}

								event.preventDefault();
								setDragging(handle);
							}}
						>
							{handle === "in" ? "⟨" : "⟩"}
						</div>
					);
				})}
			</div>

			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex items-center gap-2">
					<span className="text-xs font-bold uppercase text-subtle">{ts("trimStart")}</span>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={disabled}
						size={ButtonSize.S}
						title={ts("nudgeMinus")}
						type="button"
						onClick={() => nudge("in", -CLIP_NUDGE_SECONDS)}
					/>
					<span className="whitespace-nowrap rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-subtle">
						{formatSeconds(clip.inSeconds)}
					</span>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={disabled}
						size={ButtonSize.S}
						title={ts("nudgePlus")}
						type="button"
						onClick={() => nudge("in", CLIP_NUDGE_SECONDS)}
					/>
				</div>

				<span className="rounded-full bg-accent-tint px-2.5 py-1 text-xs font-semibold text-ink">
					{ts("kept", { kept: formatSeconds(kept), total: formatSeconds(duration) })}
				</span>

				<div className="flex items-center gap-2">
					<span className="text-xs font-bold uppercase text-subtle">{ts("trimEnd")}</span>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={disabled}
						size={ButtonSize.S}
						title={ts("nudgeMinus")}
						type="button"
						onClick={() => nudge("out", -CLIP_NUDGE_SECONDS)}
					/>
					<span className="whitespace-nowrap rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-subtle">
						{formatSeconds(clip.outSeconds)}
					</span>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={disabled}
						size={ButtonSize.S}
						title={ts("nudgePlus")}
						type="button"
						onClick={() => nudge("out", CLIP_NUDGE_SECONDS)}
					/>
				</div>
			</div>

			<p className="text-xs text-subtle">{ts("trimHint", { min: MIN_CLIP_SECONDS })}</p>

			{/* The voice on its own line, against the same axis as the picture above it. */}
			<VoiceTrack
				audioRef={narrationRef}
				clip={clip}
				disabled={disabled}
				durationSeconds={duration}
				narrationUrl={scene.narrationAssetUrl}
				onChange={onChange}
				onCommit={onCommit}
			/>
		</section>
	);
};
